import { enableMapSet } from "immer";
import { immer } from "zustand/middleware/immer";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

enableMapSet();

type ChatRoomStateMap = Map<string, ChatRoomState>;
type ChatRoomMap = Map<string, ChatRoom>;

export interface ScrollInfo {
  action?: string;
  scrollTopValue?: number;
  scrollHeightValue?: number;
}
interface ChatRoomState {
  uiState: ChatRoomUIState;
  chatRoomMessages: ChatRoomMessage[];
}

interface ChatRoomUIState {
  isLoading: boolean;
  isInitialPageLoaded: boolean;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  scrollInfo: ScrollInfo;
  firstMessageId: number | null;
  lastMessageId: number | null;
  shouldShowLatestMessageBtn: boolean;
  topMessageId: number | null;
  activeMessageId: string | null;
}

interface ChatRoomStateActions {
  setChatRoomMessages: (uuid: string, messages: ChatRoomMessage[]) => void;
  appendChatRoomMessages: (
    uuid: string,
    newMessages: ChatRoomMessage[],
    isScrolledDown: boolean,
  ) => void;
  setIsLoading: (uuid: string, isLoading: boolean) => void;
  setIsInitialPageLoaded: (uuid: string, isLoading: boolean) => void;
  setHasNextPage: (uuid: string, hasNextPage: boolean) => void;
  setHasPrevPage: (uuid: string, hasPrevPage: boolean) => void;
  setScrollInfo: (uuid: string, scrollInfo: ScrollInfo) => void;
  setFirstMessageId: (uuid: string, id: number | null) => void;
  setLastMessageId: (uuid: string, id: number | null) => void;
  setShouldShowLatestMessageBtn: (
    uuid: string,
    shouldShowLatestMessageBtn: boolean,
  ) => void;
  setTopMessageId: (uuid: string, id: number | null) => void;
  setActiveMessageId: (uuid: string, id: string | null) => void;
  resetChatRoom: (uuid: string) => void;
  clearSpotlightedMessages: (uuid: string) => void;
}

export interface ChatRoomActions {
  fetchChatRoom: (uuid: string) => Promise<void>;
  updateChatRoom: (chatRoom: ChatRoom) => void;
  appendChatRoom: (chatRoom: ChatRoom) => void;
  removeChatRoom: (uuid: string) => void;
  markChatRoomAsRead: (uuid: string) => void;
}

export interface ChatRoomsStoreProps {
  chatRoomMap: ChatRoomMap;
  chatRoomStateMap: ChatRoomStateMap;
  createNewChatRoom: (uuid: string) => ChatRoomUIState;
  actions: ChatRoomActions;
  roomActions: ChatRoomStateActions;
}

const initialChatRoomUiState: ChatRoomUIState = {
  isLoading: false,
  hasNextPage: false,
  hasPrevPage: false,
  scrollInfo: {},
  firstMessageId: null,
  lastMessageId: null,
  shouldShowLatestMessageBtn: false,
  topMessageId: null,
  isInitialPageLoaded: false,
  activeMessageId: null,
};

const initialChatRoomState: ChatRoomState = {
  uiState: initialChatRoomUiState,
  chatRoomMessages: [],
};

const MAX_MESSAGES_THRESHOLD = 60;
const MESSAGES_THRESHOLD = 40;

export const createChatRoomsSlice = immer<ChatRoomsStoreProps>(set => ({
  chatRoomMap: new Map(),
  chatRoomStateMap: new Map(),
  createNewChatRoom: uuid => {
    set(state => {
      state.chatRoomStateMap.set(uuid, { ...initialChatRoomState });
    });
    return initialChatRoomUiState;
  },
  actions: {
    fetchChatRoom: async (uuid: string) => {
      const newChatRoomState = {
        ...initialChatRoomState,
        uiState: {
          ...initialChatRoomUiState,
          isLoading: true,
        },
      };
      set(state => {
        state.chatRoomStateMap.set(uuid, newChatRoomState);
      });
      try {
        const data = await reactQueryGet<{ chat_room: ChatRoom }>(
          internalApi.chatRooms.show({ uuid }),
        );
        if (data.chat_room) {
          set(state => {
            const chatRoom = data.chat_room;
            state.chatRoomMap.set(uuid, chatRoom);
            const chatRoomState = state.chatRoomStateMap.get(uuid);
            if (chatRoomState) {
              chatRoomState.uiState.isLoading = false;
            }
          });
        }
      } catch (error) {
        set(state => {
          const chatRoomState = state.chatRoomStateMap.get(uuid);
          if (chatRoomState) {
            chatRoomState.uiState.isLoading = false;
          }
        });
        console.error(error);
      }
    },
    updateChatRoom: (chatRoom: ChatRoom) =>
      set(state => {
        const currentChatRoom = state.chatRoomMap.get(chatRoom.uuid);
        if (currentChatRoom) {
          state.chatRoomMap.set(chatRoom.uuid, {
            ...currentChatRoom,
            ...chatRoom,
          });
        }
      }),
    appendChatRoom: (chatRoom: ChatRoom) =>
      set(state => {
        state.chatRoomMap.delete(chatRoom.uuid);
        state.chatRoomMap.set(chatRoom.uuid, chatRoom);
      }),
    removeChatRoom: (uuid: string) =>
      set(state => {
        state.chatRoomMap.delete(uuid);
      }),
    markChatRoomAsRead: (uuid: string) =>
      set(state => {
        const chatRoom = state.chatRoomMap.get(uuid);
        if (chatRoom) {
          chatRoom.unread_messages_count = 0;
        }
      }),
  },
  roomActions: {
    setChatRoomMessages: (uuid, messages) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.chatRoomMessages = messages;
        }
      }),
    appendChatRoomMessages: (uuid, newMessages, isScrolledDown) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          let messages = [...chatRoomState.chatRoomMessages];
          // Memory Optimization
          if (messages.length > MAX_MESSAGES_THRESHOLD) {
            if (isScrolledDown) {
              messages = messages.slice(0, MESSAGES_THRESHOLD);
              chatRoomState.uiState.firstMessageId = messages[0].id;
              chatRoomState.uiState.hasPrevPage = true;
            } else {
              chatRoomState.uiState.topMessageId = newMessages[0].id;
              messages = messages.slice(0, MESSAGES_THRESHOLD);
              chatRoomState.uiState.lastMessageId =
                messages[messages.length - 1].id;
              chatRoomState.uiState.hasNextPage = true;
            }
          } else {
            chatRoomState.uiState.topMessageId = null;
          }
          chatRoomState.chatRoomMessages = [...newMessages, ...messages];
        }
      }),
    setIsLoading: (uuid, isLoading) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.isLoading = isLoading;
        }
      }),
    setIsInitialPageLoaded: (uuid, isLoaded) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.isInitialPageLoaded = isLoaded;
        }
      }),
    setHasNextPage: (uuid, hasNextPage) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.hasNextPage = hasNextPage;
        }
      }),
    setHasPrevPage: (uuid, hasPrevPage) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.hasPrevPage = hasPrevPage;
        }
      }),
    setScrollInfo: (uuid, scrollInfo) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.scrollInfo = scrollInfo;
        }
      }),
    setFirstMessageId: (uuid, id) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.firstMessageId = id;
        }
      }),
    setLastMessageId: (uuid, id) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.lastMessageId = id;
        }
      }),
    setShouldShowLatestMessageBtn: (uuid, shouldShowLatestMessageBtn) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.shouldShowLatestMessageBtn =
            shouldShowLatestMessageBtn;
        }
      }),
    setTopMessageId: (uuid, id) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.topMessageId = id;
        }
      }),
    setActiveMessageId: (uuid, id) =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.uiState.activeMessageId = id;
        }
      }),
    resetChatRoom: uuid =>
      set(state => {
        state.chatRoomStateMap.set(uuid, { ...initialChatRoomState });
      }),
    clearSpotlightedMessages: uuid =>
      set(state => {
        const chatRoomState = state.chatRoomStateMap.get(uuid);
        if (chatRoomState) {
          chatRoomState.chatRoomMessages = chatRoomState.chatRoomMessages.map(
            message => ({
              ...message,
              spotlighted: false,
            }),
          );
        }
      }),
  },
}));
